import { lazy, Suspense, useEffect, type CSSProperties } from 'react'
import { Routes, Route, Outlet, Link, Navigate, useLocation } from 'react-router-dom'
import { FileQuestion, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useOrgBrandingStore } from '@/stores/orgBrandingStore'
import { useIsOrganizer, useHasAnyOrgMembership } from '@/hooks/useIsOrganizer'
import UserAvatarMenu from '@/components/UserAvatarMenu'
import EnvironmentIndicator from '@/components/EnvironmentIndicator'
import StaleAppVersionBoundary from '@/components/StaleAppVersionBoundary'
import { ConfirmProvider, AcknowledgeConfirmProvider } from '@/components/ConfirmDialog'
import { clearChunkHealMarkerAfterHealthyRender } from '@/lib/chunkReloadRecovery'
import { getThemeCssVariables, getEffectiveOrganizationLogoSize } from '../../shared/organizationTheme'

// ── 各ページは lazy import（初期バンドル削減・iOS Safari リロード対策）──

const LandingPage             = lazy(() => import('@/pages/LandingPage'))
const Login                   = lazy(() => import('@/pages/Login'))
const PlayerLogin             = lazy(() => import('@/pages/PlayerLogin'))
const Register                = lazy(() => import('@/pages/Register'))
const WelcomePage             = lazy(() => import('@/pages/WelcomePage'))
const InviteAcceptPage        = lazy(() => import('@/pages/InviteAcceptPage'))
const EventList               = lazy(() => import('@/pages/admin/EventList'))
const EventForm               = lazy(() => import('@/pages/admin/EventForm'))
const EventPublicPageEditPage = lazy(() => import('@/pages/admin/EventPublicPageEditPage'))
const EventDetail             = lazy(() => import('@/pages/admin/EventDetail'))
const FullscreenMatchDisplay  = lazy(() => import('@/pages/admin/FullscreenMatchDisplay'))
const PointsPage              = lazy(() => import('@/pages/admin/PointsPage'))
const OrganizationSettingsPage= lazy(() => import('@/pages/admin/OrganizationSettingsPage'))
const PlayerEventPage         = lazy(() => import('@/pages/player/PlayerEventPage'))
const PlayerMyPage            = lazy(() => import('@/pages/player/PlayerMyPage'))
const PrivateMatchPage        = lazy(() => import('@/pages/player/PrivateMatchPage'))
const PublicEventListPage     = lazy(() => import('@/pages/PublicEventListPage'))
const PlayerSignupPage        = lazy(() => import('@/pages/player/PlayerSignupPage'))
const EventCheckinPage        = lazy(() => import('@/pages/player/EventCheckinPage'))
const SystemAdminPage         = lazy(() => import('@/pages/system/SystemAdminPage'))
const SystemAdminUserPage     = lazy(() => import('@/pages/system/SystemAdminUserPage'))
const GuideLayout             = lazy(() => import('@/pages/guide/GuideLayout'))
const GuideIndex              = lazy(() => import('@/pages/guide/GuideIndex'))
const GuidePage               = lazy(() => import('@/pages/guide/GuidePage'))

// ── 認証保護ルート ────────────────────────────────────────────

function ProtectedRoute() {
  const token = useAuthStore((s) => s.token)
  if (!token) return <Navigate to="/" replace />
  return <Outlet />
}

// ── 主催者専用ルート（OWNER または MEMBER の管理者ロール所属者のみ通す）─────────
// プレイヤー（マイページのみ・組織所属なし）や RESULT_ONLY のみのユーザが、
// /events/new 等の編集系ルートを直接 URL で開けないようガード。
// useIsOrganizer フックで /api/auth/me を確認し、false ならランディングへリダイレクト。
function OrganizerRoute() {
  const isOrganizer = useIsOrganizer()
  if (isOrganizer === null) return <RouteFallback />
  if (isOrganizer === false) return <Navigate to="/" replace />
  return <Outlet />
}

// ── システム管理者専用ルート ─────────────────────────────────
function SystemAdminRoute() {
  const user = useAuthStore((s) => s.user)
  if (!user?.isSystemAdmin) return <Navigate to="/events" replace />
  return <Outlet />
}

// ── 団体所属ユーザのルート（OWNER / MEMBER / RESULT_ONLY 全て通す）─────────
// /events 一覧と /events/:id 詳細は RESULT_ONLY でも閲覧+結果入力できる。
// 編集系の /events/new / /events/:id/edit は OrganizerRoute でさらに絞る。
function MemberRoute() {
  const hasMembership = useHasAnyOrgMembership()
  if (hasMembership === null) return <RouteFallback />
  if (hasMembership === false) return <Navigate to="/" replace />
  return <Outlet />
}

// ── 管理画面レイアウト ────────────────────────────────────────
// 主催者権限なし（LINE プレイヤー等が /settings?tab=account を開いたケース）はヘッダーを赤系にして
// 「主催者画面ではなく個人アカウント設定にいる」ことを視覚的に区別する

function AdminLayout() {
  const { user } = useAuthStore()
  const isOrganizer = useIsOrganizer()
  const isNonOrganizer = isOrganizer === false  // null（読み込み中）の間はデフォルト色で
  const branding = useOrgBrandingStore((s) => s.branding)
  const ensureBranding = useOrgBrandingStore((s) => s.ensure)

  // 主催者は団体テーマ（カラー・ロゴ）をシェルに反映。非主催者は標準のまま。
  useEffect(() => {
    if (isOrganizer) ensureBranding()
  }, [isOrganizer, ensureBranding])

  // テーマカラーは scope の CSS 変数で上書き（配下の bg-primary / btn-primary / アクティブ状態が追従）。
  // ヘッダー背景: 「テーマカラーを使用しない」(useThemeColorAsSurface=false) の主催者は白背景、
  // それ以外（テーマカラー使用 or 読み込み中）は暗いグレー据え置き。非主催者は赤(bg-primary)。
  const themeVars = getThemeCssVariables(branding) as CSSProperties
  const surfaceOff = !isNonOrganizer && branding?.useThemeColorAsSurface === false
  const headerBg = isNonOrganizer
    ? 'bg-primary text-white'
    : surfaceOff
      ? 'bg-white text-neutral-800 border-b border-neutral-200'
      : 'bg-slate-800 text-white'
  const hasCustomLogo = !!branding?.brandingLogoUrl
  // デフォルトロゴは白背景なら暗色版、暗背景なら明色版。
  const headerLogo = branding?.brandingLogoUrl ?? (surfaceOff ? '/taiari_logo_dark_trans.png' : '/taiari_logo_admin.png')
  // 団体ロゴは暗いヘッダー背景で白箱化しないよう明色チップに載せる（白背景ヘッダーではチップ不要）。標準/大はカスタムロゴのみ反映。
  const logoLarge = hasCustomLogo && getEffectiveOrganizationLogoSize(branding?.brandingLogoSize) === 'large'
  const logoClass = `${logoLarge ? 'h-8 max-w-[220px]' : 'h-4 max-w-[120px]'} w-auto object-contain`

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50" style={themeVars}>
      <header className={`${headerBg} px-4 py-3 flex items-center gap-2 shadow-card`}>
        <Link to={isNonOrganizer ? '/' : '/events'} className="hover:opacity-90 flex-1 min-w-0">
          {hasCustomLogo && !surfaceOff ? (
            <span className="inline-flex items-center rounded bg-white/95 px-1.5 py-0.5">
              <img src={headerLogo} alt="TAIARI" className={logoClass} />
            </span>
          ) : (
            <img src={headerLogo} alt="TAIARI" className={logoClass} />
          )}
        </Link>
        {user && <UserAvatarMenu context="admin" onLight={surfaceOff} />}
      </header>
      <main className="flex-1 container mx-auto px-4 py-4 max-w-screen-xl">
        <Outlet />
      </main>
    </div>
  )
}

// ── ローディング表示（Suspense fallback）──────────────────────

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <Loader2 size={28} className="animate-spin text-neutral-400" />
    </div>
  )
}

function ChunkHealMarkerReset() {
  const location = useLocation()

  useEffect(() => {
    clearChunkHealMarkerAfterHealthyRender()
  }, [location.key])

  return null
}

// ── 404 ──────────────────────────────────────────────────────

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-3 text-neutral-400 px-6 text-center">
      <FileQuestion size={48} className="text-neutral-300" />
      <p className="text-base font-bold text-neutral-600">このページは存在しません</p>
      <p className="text-sm">URLが間違っているか、ページが削除された可能性があります。</p>
      <Link to="/" className="btn-primary mt-1">トップへ戻る</Link>
    </div>
  )
}

// ── ルーティング ─────────────────────────────────────────────

export default function App() {
  return (
    <>
    <EnvironmentIndicator />
    <StaleAppVersionBoundary />
    <ConfirmProvider>
    <AcknowledgeConfirmProvider>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* ランディングページ */}
      <Route index element={<LandingPage />} />

      {/* プレイヤー向けページ（認証不要） */}
      <Route path="event/p/:token"                      element={<PlayerEventPage />} />
      <Route path="public/events"                       element={<PublicEventListPage />} />
      <Route path="p/signup/:eventUuid"                 element={<PlayerSignupPage />} />
      <Route path="public/events/:eventUuid"            element={<PlayerSignupPage />} />
      <Route path="event-checkin/:eventUuid"            element={<EventCheckinPage />} />
      <Route path="player/:myPageToken"                 element={<PlayerMyPage />} />
      <Route path="player/:myPageToken/match/:matchId"  element={<PrivateMatchPage />} />
      <Route path="register"                            element={<Register />} />

      {/* ログインページ（直URL限定） */}
      <Route path="login" element={<Login />} />
      <Route path="player-login" element={<PlayerLogin />} />

      {/* 初回プレイヤー名入力（要認証・hasSetPlayerName=false で誘導される） */}
      <Route path="welcome" element={<WelcomePage />} />

      {/* 主催者団体の招待受諾（招待メールのリンク・未ログインでも開ける） */}
      <Route path="invite" element={<InviteAcceptPage />} />

      {/* ガイド（認証不要） */}
      <Route element={<GuideLayout />}>
        <Route path="guide"                  element={<GuideIndex />} />
        <Route path="guide/organizer"        element={<GuideIndex audience="organizer" />} />
        <Route path="guide/player"           element={<GuideIndex audience="player" />} />
        <Route path="guide/:audience/:slug"  element={<GuidePage />} />
      </Route>

      {/* 認証必須の管理画面 */}
      <Route element={<ProtectedRoute />}>
        {/* 団体メンバー領域（OWNER/MEMBER/RESULT_ONLY 全員が閲覧可、編集系は中で制限）*/}
        <Route element={<MemberRoute />}>
          {/* 全画面投影モード（AdminLayout の外側でフルスクリーン描画。読み取り表示なので RESULT_ONLY も可） */}
          <Route path="events/:id/fullscreen" element={<FullscreenMatchDisplay />} />

          <Route element={<AdminLayout />}>
            <Route path="events"     element={<EventList />} />
            <Route path="events/:id" element={<EventDetail />} />
          </Route>
        </Route>

        {/* 管理者(OWNER+MEMBER)のみアクセス可能なルート */}
        <Route element={<OrganizerRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="events/new"          element={<EventForm />} />
            <Route path="events/:id/edit"     element={<EventForm />} />
            <Route path="events/:id/event-page/edit" element={<EventPublicPageEditPage />} />
            <Route path="points"              element={<PointsPage />} />
          </Route>
        </Route>

        {/* システム管理者のみアクセス可能な横断管理ルート */}
        <Route element={<SystemAdminRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="system-admin"      element={<SystemAdminPage />} />
            <Route path="system-admin/:id"  element={<SystemAdminUserPage />} />
          </Route>
        </Route>

        {/* /settings は非主催者ユーザー（LINE プレイヤーなど）もアクセス可。
            アカウントタブで認証手段の管理ができる。組織絡みのタブはページ内で制御 */}
        <Route element={<AdminLayout />}>
          <Route path="settings" element={<OrganizationSettingsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
    <ChunkHealMarkerReset />
    </Suspense>
    </AcknowledgeConfirmProvider>
    </ConfirmProvider>
    </>
  )
}
